<template>
  <div class="navigation shadow">
    <div class="py1 px4 flex justify_between">
      <organization v-if="isHome"/>

      <div v-else class="flex">
        <router-link
          name="dashboardlink"
          class="mya py1 flex pr2"
          style="font-size: 24px;"
          :to="{name: 'dashboard'}"
        ><arrow-left class="mya"/></router-link>

        <h4 class="medium my2">{{ $router.currentRoute.meta.text }}</h4>
      </div>

      <slot name="action">
        <svg
          v-if="isHome && expanded"
          @click="expanded = false"
          class="mya"
          viewBox="0 0 24 24"
        >
          <path
            fill="#000000"
            :d="`
            M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,
            17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z
            `"
          />
        </svg>

        <svg
          v-else-if="isHome"
          @click="expanded = true"
          class="mya action"
          viewBox="0 0 24 24"
        >
          <path
            fill="#000000"
            d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z"
          />
        </svg>
      </slot>
    </div>

    <hr class="nav_item">

    <collapse v-model="expanded" flexible-height>
      <slot name="content">
        <div>
          <div class="flex justify_between px4">
            <user @click.native="$router.push('/edit_profile')" />
            <toggle-arrow v-model="showUsers" class="mya"/>
          </div>
          <hr v-if="showUsers" class="uncollapsed_divider">
          <hr v-else class="nav_item">

          <collapse
            flexible-height :value="showUsers"
            class="white_background px4">
            <colleagues v-if="expanded"/>
          </collapse>

          <hr v-if="showUsers" class="uncollapsed_divider">

          <router-link
            v-for="navigationLink in navigationLinks"
            :key="navigationLink.name"
            :to="{name: navigationLink.name}"
          >
            <div class="flex justify_between px4">
              <span class="flex">
                <icon class="nav_icon" :icon-name="navigationLink.meta.icon"/>
                <h4 class="mya bold">
                  {{ navigationLink.meta.text }}
                </h4>
              </span>

              <arrow-right class="mya"/>
            </div>
            <hr class="nav_item">
          </router-link>

          <div class="flex justify_between px4">
            <div class="flex">
              <icon globe class="nav_icon"/>
              <h4 class="mya bold">{{ $t('cms.header.Language') }}</h4>
            </div>

            <div class="flex">
              <h4 class="mya mr2 bold">{{ currentLocaleName }}</h4>
              <toggle-arrow v-model="showLanguages" class="mya"/>
            </div>
          </div>

          <hr v-if="showLanguages" class="uncollapsed_divider">

          <collapse
            flexible-height :value="showLanguages"
            class="white_background px4">
            <div
              v-for="(language, locale, index) in languages"
              :key="language.locale"
              @click="selectLocale(language.locale)"
            >
              <div class="flex justify_between">
                <div class="flex">
                  <div v-if="currentLocale === locale"
                    class="selected_indicator"
                  />
                  <div v-else
                       class="selected_indicator"
                       style="opacity: 0"
                  />
                  <h4 class="mya bold">{{ language.name }}</h4>
                </div>
                <icon
                  fileType="svg"
                  :icon-name="language.icon"
                  class="nav_icon"
                />
              </div>
              <hr
                v-if="Object.keys(languages).length - 1 !== index"
                class="uncollapsed_divider"
              />
            </div>
          </collapse>

          <hr v-if="showLanguages" class="uncollapsed_divider">

          <hr v-else class="nav_item">

          <span @click="askForLogout = !askForLogout" class="action">
            <div class="flex px4">
              <icon logout class="nav_icon"/>
              <h4 class="mya bold">{{ $t('cms.menu.Logout') }}</h4>
            </div>
          </span>

          <collapse :value="askForLogout" class="px4">
            <span style="font-style: italic;">{{ $t('cms.menu.LogoutConfirmQuestion') }}</span>

            <div class="mt2 flex row">
              <button
                type="button"
                class="white_background bold box grow mr1"
                @click="logout"
              >
                {{ $t('cms.menu.LogoutConfirmButton') }}
              </button>

              <button
                type="button"
                class="white_background bold box grow ml1"
                @click="askForLogout = !askForLogout"
              >
                {{ $t('cms.menu.LogoutCancelButton') }}
              </button>
            </div>
          </collapse>
        </div>
      </slot>
    </collapse>
  </div>
</template>

<script>
import User from './User.vue';
import colleagues from './Colleagues.vue';
import store from '@/store';
import Organization from './Organization.vue';
import AuthMixin from '@/mixins/auth_mixin';
import CONSTANTS from '@/constants/constants';
import vueI18n from '@/plugins/i18n';

export default {
  name: 'navigation',

  mixins: [AuthMixin],

  components: { Organization, colleagues, User },

  data() {
    return {
      expanded: false,
      showLanguages: false,
      currentLocale: vueI18n.locale,
      showUsers: false,
      askForLogout: false,
    };
  },

  methods: {
    selectLocale(locale) {
      this.currentLocale = locale;
      vueI18n.setLocale(locale);
    },
  },

  computed: {
    isAdmin() {
      return store.state.user.isAdmin;
    },

    currentLocaleName() {
      return this.languages[this.currentLocale].name;
    },

    languages() {
      return CONSTANTS.languages;
    },

    isHome() {
      return this.$router.currentRoute.name === 'dashboard';
    },

    navigationLinks() {
      return this.$router.options.routes
        .filter(({ meta }) => meta.includeInNavigation
            // Filter admin only routes if user is not admin
            && !(meta.adminOnly && !this.isAdmin));
    },
  },
};
</script>

<style lang="scss">
  .navigation {
    max-width: $app_screen_width;
    width: 100%;
    background-color: #f2f2f2;
  }

  hr.nav_item {
    color: white;
    width: unset;
    margin-top: 0;
    margin-bottom: 0;
    margin-left: $base_spacing * 4;
    margin-right: $base_spacing * 4;
  }

  .uncollapsed_divider {
    margin: 0;
    margin-top: -1px;
  }

  .selected_indicator {
    margin-bottom: auto;
    margin-top: auto;

    margin-left: 16px; // icon width - ( indicator width + border)
    margin-right: $base_spacing * 2;

    height: $size_online_circle;
    width: $size_online_circle;

    border: solid 2px white;
    border-radius: 99px;

    background: #00C853;
  }

  .nav_icon {
    padding-top: $base_spacing * 2;
    padding-bottom: $base_spacing * 2;
    padding-right: $base_spacing * 2;
    object-fit: contain;
  }

</style>
